<template>

  <div>
    <!-- Data loading spinner -->
    <template v-if="isDataLoading">
      <v-progress-circular indeterminate :size="21" color="white" />
    </template>

    <template v-else>
      <v-stepper vertical non-linear ref="stepper" class="delivery-stepper-step-container" v-model="activeStep" v-if="route">
        <template v-for="(step, stepIndex) in stepperOutline">

          <!------------------------------------------------------------------->
          <!-- Claim Vehicle step -->
          <!------------------------------------------------------------------->
          <template v-if="step.type === 'claim-vehicle'">
            <v-stepper-step
                ref="step1"
                :class="{ deliveryStepComplete: isStepComplete(1), deliveryStepActive: getActiveStep() === 1 }"
                :editable="isStepAccessible(1)"
                :key="`claim-vehicle-title-${stepIndex}`"
                step="1">
              {{ claimVehicleStepTitle }}
            </v-stepper-step>
            <v-stepper-content
                :key="`claim-vehicle-content-${stepIndex}`"
                step="1">
              <delivery-step-claim-vehicle
                  :hq="hq"
                  :route="route"
                  v-on:completed="goToNextStep" />
            </v-stepper-content>
          </template>

          <!------------------------------------------------------------------->
          <!-- Head to Shipper step -->
          <!------------------------------------------------------------------->
          <template v-if="step.type === 'to-shipper'">
            <v-stepper-step
                :ref="`step${stepIndex + 1}`"
                :class="{ deliveryStepComplete: isStepComplete(stepIndex + 1), deliveryStepActive: getActiveStep() === stepIndex + 1 }"
                :editable="isStepAccessible(stepIndex + 1)"
                :key="`to-shipper-title-${stepIndex}`"
                :step="stepIndex + 1">
              Head to Shipper
              <small>
                <span class="pr-2" v-if="step.order_numbers.length > 1">To pick-up orders</span>
                <span class="pr-2" v-else>To pick-up order</span>
                <span class="orders-as-string">{{ step.order_numbers.join(', ') }}</span>
              </small>
            </v-stepper-step>
            <v-stepper-content
                :key="`to-shipper-content-${stepIndex}`"
                :step="stepIndex + 1">
              <delivery-step-head-to-shipper
                  :route="route"
                  :orders="getOrders(step.order_numbers)"
                  :stepNumber="stepIndex + 1"
                  v-on:completed="goToNextStep" />
            </v-stepper-content>
          </template>

          <!------------------------------------------------------------------->
          <!-- Pickup Order step -->
          <!------------------------------------------------------------------->
          <template v-if="step.type === 'pickup'">
            <v-stepper-step
                :ref="`step${stepIndex + 1}`"
                :class="{ deliveryStepComplete: isStepComplete(stepIndex + 1), deliveryStepActive: getActiveStep() === stepIndex + 1 }"
                :editable="isStepAccessible(stepIndex + 1)"
                :key="`pickup-title-${step.order_number}`"
                :step="stepIndex + 1">
              Pick-up Order {{ step.order_number }}
              <small>Obtain and Verify</small>
            </v-stepper-step>
            <v-stepper-content
                :key="`pickup-content-${step.order_number}`"
                :step="stepIndex + 1">
              <delivery-step-pickup-order
                  :route="route"
                  :order="getOrder(step.order_number)"
                  :stepNumber="stepIndex + 1"
                  :isStepActive="activeStep === stepIndex + 1"
                  v-on:completed="goToNextStep" />
            </v-stepper-content>
          </template>

          <!------------------------------------------------------------------->
          <!-- Head to Receiver step -->
          <!------------------------------------------------------------------->
          <template v-if="step.type === 'to-receiver'">
            <v-stepper-step
                :ref="`step${stepIndex + 1}`"
                :class="{ deliveryStepComplete: isStepComplete(stepIndex + 1), deliveryStepActive: getActiveStep() === stepIndex + 1 }"
                :editable="isStepAccessible(stepIndex + 1)"
                :key="`to-receiver-title-${stepIndex}`"
                :step="stepIndex + 1">
              Head to Receiver
              <small>
                <span class="pr-2" v-if="step.order_numbers.length > 1">To deliver orders</span>
                <span class="pr-2" v-else>To deliver order</span>
                <span class="orders-as-string">{{ step.order_numbers.join(', ') }}</span>
              </small>
            </v-stepper-step>
            <v-stepper-content
                :key="`to-receiver-content-${stepIndex}`"
                :step="stepIndex + 1">
              <delivery-step-head-to-receiver
                  :route="route"
                  :orders="getOrders(step.order_numbers)"
                  :stepNumber="stepIndex + 1"
                  v-on:completed="goToNextStep" />
            </v-stepper-content>
          </template>

          <!------------------------------------------------------------------->
          <!-- Deliver Order step -->
          <!------------------------------------------------------------------->
          <template v-if="step.type === 'dropoff'">
            <v-stepper-step
                :ref="`step${stepIndex + 1}`"
                :class="{ deliveryStepComplete: isStepComplete(stepIndex + 1), deliveryStepActive: getActiveStep() === stepIndex + 1 }"
                :editable="isStepAccessible(stepIndex + 1)"
                :key="`deliver-title-${step.order_number}`"
                :step="stepIndex + 1">
              Deliver Order {{ step.order_number }}
              <small>Obtain Receiver Info</small>
            </v-stepper-step>
            <v-stepper-content
                :key="`deliver-content-${step.order_number}`"
                :step="stepIndex + 1">
              <delivery-step-deliver-order
                  :route="route"
                  :order="getOrder(step.order_number)"
                  :stepNumber="stepIndex + 1"
                  :isStepActive="activeStep === stepIndex + 1"
                  v-on:completed="goToNextStep" />
            </v-stepper-content>
          </template>

          <!------------------------------------------------------------------->
          <!-- Return to HQ step -->
          <!------------------------------------------------------------------->
          <template v-if="step.type === 'return-to-hq'">
            <v-stepper-step
                :ref="`step${stepIndex + 1}`"
                :class="{ deliveryStepComplete: isStepComplete(stepIndex + 1), deliveryStepActive: getActiveStep() === stepIndex + 1 }"
                :editable="isStepAccessible(stepIndex + 1)"
                :key="`return-to-hq-title-${stepIndex}`"
                :step="stepIndex + 1">
              {{ returnToHqStepTitle }}
            </v-stepper-step>
            <v-stepper-content
                :key="`return-to-hq-content-${stepIndex}`"
                :step="stepIndex + 1">
              <delivery-step-return-to-hq
                  :hq="hq"
                  :route="route"
                  :stepNumber="stepIndex + 1"
                  v-on:completed="handleCompletedDelivery" />
            </v-stepper-content>
          </template>

        </template>
      </v-stepper>
    </template>
  </div>

</template>

<script>
/* eslint-disable no-unneeded-ternary */
import DeliveryStepClaimVehicle from '@/components/deliveries/DeliveryStepClaimVehicle'
import DeliveryStepHeadToReceiver from '@/components/deliveries/DeliveryStepHeadToReceiver'
import DeliveryStepHeadToShipper from '@/components/deliveries/DeliveryStepHeadToShipper'
import DeliveryStepDeliverOrder from '@/components/deliveries/DeliveryStepDeliverOrder'
import DeliveryStepPickupOrder from '@/components/deliveries/DeliveryStepPickupOrder'
import DeliveryStepReturnToHq from '@/components/deliveries/DeliveryStepReturnToHq'

export default {
  name: 'DeliveryStepper',

  components: {
    DeliveryStepClaimVehicle,
    DeliveryStepHeadToReceiver,
    DeliveryStepHeadToShipper,
    DeliveryStepDeliverOrder,
    DeliveryStepPickupOrder,
    DeliveryStepReturnToHq
  },

  props: {
    hq: {
      type: Object,
      required: true
    },
    route: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      isDataLoading: false,
      stepperOutline: null,
      activeStep: 0
    }
  },

  computed: {
    // The route delivery steps (pickups, dropoffs)
    deliverySteps () {
      return (this.route.route_info) ? this.route.route_info.steps : []
    },
    // The total number of delivery steps
    deliveryStepCount () {
      return this.deliverySteps.reduce((sum, step) => sum + 1 + step.order_ids.length, 0)
    },
    // The orders of the Route
    orders () {
      return this.route.orders || []
    },
    // The "claim vehicle" step title
    claimVehicleStepTitle () {
      return 'Claim Vehicle'
    },
    // The "return to hq" step title
    returnToHqStepTitle () {
      return 'Return to Lot'
    }
  },

  beforeMount () {
    // Build stepper outline
    this.buildStepperOutline()
    this.activeStep = this.getActiveStep()
  },

  mounted () {
    // Scroll to active step
    this.scrollToActiveStep()
  },

  methods: {
    // Generate the full stepper "outline"
    buildStepperOutline () {
      this.stepperOutline = []

      // Populate 'claim-vehicle' step
      this.stepperOutline.push({
        type: 'claim-vehicle'
      })

      // Populate delivery steps
      for (const step of this.deliverySteps) {
        const prepStep = (step.type === 'pickup') ? 'to-shipper' : 'to-receiver'
        const orderNumbers = step.order_numbers || []
        this.stepperOutline.push({
          type: prepStep,
          order_numbers: orderNumbers
        })
        for (const orderNumber of orderNumbers) {
          this.stepperOutline.push({
            type: step.type,
            order_number: orderNumber
          })
        }
      }

      // Populate 'return-to-hq' step
      this.stepperOutline.push({
        type: 'return-to-hq'
      })
    },

    // Returns a single order
    getOrder (orderNum) {
      return this.orders.find(order => order.thirdparty_order_number === orderNum)
    },

    // Returns array of orders
    getOrders (orderNums) {
      return this.orders.filter(order => this.$root.utils.includes(orderNums, order.thirdparty_order_number))
    },

    // Returns the step that should be actively handled in the delivery
    getActiveStep () {
      if (!this.route.on_delivery_at) {
        return 0
      } else if (this.isStepComplete(this.stepperOutline.length)) {
        return this.stepperOutline.length
      } else {
        for (let i = 1; i < this.stepperOutline.length + 1; i++) {
          if (!this.isStepComplete(i)) return i
        }
      }
    },

    // TODO - We need to handle the case of another step opened (with the delivery active step closed).
    //        i.e. Close step, expand active one, then scroll to it.
    // Scroll to the active delivery step
    scrollToActiveStep () {
      const activeDeliveryStep = this.getActiveStep()
      const lastStep = this.deliveryStepCount + 2
      const isLastStepCompleted = this.isStepComplete(lastStep)
      if ((activeDeliveryStep > 0 && activeDeliveryStep < lastStep) || (activeDeliveryStep === lastStep && !isLastStepCompleted)) {
        const stepElem = this.$refs[`step${activeDeliveryStep}`]
        if (stepElem && stepElem.length > 0) {
          const scrollContainer = document.querySelector('.main-content')
          if (scrollContainer) {
            setTimeout(() => {
              scrollContainer.scrollTo({
                top: stepElem[0].$el.offsetTop,
                behavior: 'smooth'
              })
            }, 400) // requires delay
          }
        }
      }
    },

    // Advances the stepper to the next step
    goToNextStep () {
      this.activeStep = this.activeStep + 1
    },

    // Determines if the step has completed or not
    isStepComplete (stepNumber) {
      let complete = false

      const step = this.stepperOutline[stepNumber - 1]
      const stepType = step.type

      switch (stepType) {
        case 'claim-vehicle': {
          complete = (this.route.vehicle_claimed_at) ? true : false
          break
        }
        case 'to-shipper': {
          const orderNumbers = step.order_numbers || []
          const orders = this.getOrders(orderNumbers)
          const firstOrder = (orders.length > 0) ? orders[0] : {}
          complete = (firstOrder.at_shipper_at) ? true : false
          break
        }
        case 'pickup': {
          const order = this.getOrder(step.order_number)
          complete = (order.picked_up_at) ? true : false
          break
        }
        case 'to-receiver': {
          const orderNumbers = step.order_numbers || []
          const orders = this.getOrders(orderNumbers)
          const firstOrder = (orders.length > 0) ? orders[0] : {}
          complete = (firstOrder.at_receiver_at) ? true : false
          break
        }
        case 'dropoff': {
          const order = this.getOrder(step.order_number)
          complete = (order.completed_at) ? true : false
          break
        }
        case 'return-to-hq': {
          complete = (this.route.completed_at) ? true : false
          break
        }
      }

      return complete
    },

    // Determines if the step is accessible or not, by checking the previous step
    isStepAccessible (stepNumber) {
      let accessible = false

      const step = this.stepperOutline[stepNumber - 1]

      if (step.type === 'claim-vehicle') {
        accessible = (this.route.on_delivery_at) ? true : false
      } else {
        accessible = this.isStepComplete(stepNumber - 1)
      }

      return accessible
    },

    // Handle completion of all steps
    handleCompletedDelivery () {
      // const scrollContainer = document.querySelector('.main-content')
      // if (scrollContainer) {
      //   setTimeout(() => {
      //     scrollContainer.scrollTo({
      //       top: 0,
      //       behavior: 'smooth'
      //     })
      //   }, 400) // requires delay
      // }
      this.$emit('completed', this.route)
    }
  }
}
</script>

<style>
  .step {
    max-width: 600px !important;
  }

  .v-stepper__label small {
    margin-top: 6px;
  }

  .deliveryStepComplete .v-stepper__step__step {
    background-color: #20add8 !important;
    border-color: #20add8 !important;
  }

  .deliveryStepActive .v-stepper__step__step {
    background-color: #43a047 !important;
    border-color: #43a047 !important;
  }

  .deliveryStepMissed .v-stepper__step__step {
    background: hsla(1,89%,49%,0.8) !important;
  }

  .orders-as-string {
    inline-size: 180px;
    overflow-wrap: break-word;
    line-height: 18px;
  }

  .complete-step {
    margin-top: 16px !important;
    margin-bottom: 10px !important;
    padding-left: 10px;
    padding-right: 10px;
  }

  .error-message {
    color: #cc0000;
    font-style: italic;
  }

  .content-title {
    color: #c9c9c9;
    font-weight: 500;
  }

  .content-accent {
    color: #f2f2f2 !important;
    font-weight: 500;
  }

  .instruction-number {
    font-weight: 600;
    color: #ffffff;
  }

  .address-text {
    color: #9a9a9a;
    /* text-decoration: underline; */
  }

  .vehicle-text {
    color: #9a9a9a;
  }

  .instructions-text {
    color: #9a9a9a;
    font-size: 15px;
    text-align: center;
    font-style: italic;
  }

  .instructions-text {
    color: #9a9a9a;
    font-size: 15px;
    text-align: center;
    font-style: italic;
  }

  .disclaimer-text {
    color: #9a9a9a;
    font-size: 15px;
    font-style: italic;
  }

  .input-label-text {
    color: #9c9c9c;
  }

  .icon-copy {
    margin-left: 2px;
    font-size: 22px !important;
  }

  .icon-open-in-new {
    margin-right: 6px;
    font-size: 16px !important;
  }

  .icon-open-order-checklist {
    font-size: 16px !important;
  }

  @media screen and (max-width: 960px) {
  }

  @media screen and (max-width: 600px) {
    .delivery-stepper-step-container {
      border-radius: 0px !important;
    }

    .v-stepper--vertical .v-stepper__step {
      padding-left: 18px;
      padding-right: 12px;
    }

    .v-stepper--vertical .v-stepper__content {
      margin-left: 30px !important;
    }
  }
</style>
